import { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { HttpStatusCode } from "axios";
import * as Sentry from "@sentry/react";
import { useUserMe } from "@/features/login/hooks/useUserMe";
import { useGeoSpaceApi } from "@/features/map/hooks/useGeospaceSearch";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useApiClient } from "@/hooks/useApiClient";
import { saveMapServiceContractToLocalStorage } from "@/utils/mapServiceContract";
import { consumeLoginRedirect } from "@/utils/auth";
import {
  IP_NOT_ALLOWED_CODE,
  IP_NOT_ALLOWED_PATH,
} from "@/utils/ipRestriction";
import { tokenExchangeAPI } from "@/features/login/api/tokenExchangeAPI";
import {
  removeSalesforceMapSearchId,
  saveSalesforceMapSearchId,
} from "@/features/salesforceIntegration/utils/handoffStorage";
import type { ISalesforceRealEstate } from "@/types/jwt";

/** Salesforce起点のlandingPageのうち、地図画面への遷移を表す値。 */
const SALESFORCE_LANDING_PAGE_MAP = "1";

/**
 * Salesforce起点の検索対象から地図画面への遷移先パスを組み立てる。
 * 値がnullの項目はURLパラメータに含めない。
 */
const buildSalesforceMapRedirectPath = (
  realEstate: ISalesforceRealEstate | undefined
): string => {
  if (!realEstate) return "/map";

  const params = new URLSearchParams();
  if (realEstate.address !== null) {
    params.set("address", realEstate.address);
  }
  if (realEstate.chiban !== null) {
    params.set("chiban", realEstate.chiban);
  }
  if (realEstate.kaokuNumber !== null) {
    params.set("kaokuNumber", realEstate.kaokuNumber);
  }

  const query = params.toString();
  return query.length > 0 ? `/map?${query}` : "/map";
};

/**
 * JWT認証初期化フック
 * URLパラメータから認可コードを取得し、トークン交換、ユーザー情報取得、
 * 地図契約情報取得を行い、ログイン状態を確立する
 *
 * @returns {Object} 初期化状態
 * @returns {boolean} isInitializing - 初期化処理中かどうか
 */
export const useJwtAuthInitialization = (): { isInitializing: boolean } => {
  const [, setCookie] = useCookies();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userMe } = useUserMe();
  const { geospaceGetMapServiceContract } = useGeoSpaceApi();
  const { mypageTop } = useFeatureFlags();
  const { apiClient } = useApiClient();
  const [isInitializing, setIsInitializing] = useState(true);
  const hasInitialized = useRef(false);

  useEffect(() => {
    const initializeAuth = async (): Promise<void> => {
      // 既に初期化済みの場合は何もしない
      if (hasInitialized.current) {
        return;
      }

      try {
        // URLパラメータから認可コードを取得
        const code = searchParams.get("code");

        if (!code) {
          toast.error("認証情報が見つかりません");
          navigate("/login");
          return;
        }

        // 初期化フラグを立てる
        hasInitialized.current = true;

        // セキュリティ強化: URLパラメータを履歴から削除
        window.history.replaceState({}, document.title, "/auth/jwt-callback");

        // トークン交換APIを呼び出してアクセストークンとリフレッシュトークンを取得
        const tokenResponse = await tokenExchangeAPI(apiClient, code);

        // 許可IP外はネットワークを変えない限り解消しないため、専用画面へ誘導する
        if (tokenResponse.errorCode === IP_NOT_ALLOWED_CODE) {
          setIsInitializing(false);
          navigate(IP_NOT_ALLOWED_PATH, { replace: true });
          return;
        }

        // APIエラーチェック
        if (!tokenResponse.data || tokenResponse.status !== HttpStatusCode.Ok) {
          throw new Error(tokenResponse.error ?? "トークン交換に失敗しました");
        }

        const {
          access: accessToken,
          refresh: refreshToken,
          salesforceContext,
        } = tokenResponse.data;

        // トークンをCookieに保存
        setCookie("accesstoken", accessToken, { path: "/" });
        setCookie("refreshtoken", refreshToken, { path: "/" });

        // ユーザー情報を取得
        userMe();

        // 地図契約情報を取得してLocalStorageに保存
        const mapServiceContract = await geospaceGetMapServiceContract();
        saveMapServiceContractToLocalStorage(mapServiceContract ?? null);

        // ログイン状態を設定
        setCookie("isLogin", "true", { path: "/" });

        // Salesforce起点の場合、レコードIDを後続の登記取得リクエストで使うため保管する
        // （地図画面へ遷移するかどうかに関わらず保管する）。
        // 起点でないログインでは、同じタブに残った前回の値を消す。
        // 残すと無関係な物件のリクエストが前回の地図検索レコードへ紐づく
        const [primarySalesforceRealEstate] =
          salesforceContext?.realEstates ?? [];
        if (primarySalesforceRealEstate) {
          saveSalesforceMapSearchId(
            primarySalesforceRealEstate.salesforceMapSearchId
          );
        } else {
          removeSalesforceMapSearchId();
        }

        // 初期化完了、画面遷移
        setIsInitializing(false);

        // sessionStorageの汚れを残さないため、Salesforce起点でも必ず読み出して消費する
        const redirect = consumeLoginRedirect();

        const shouldNavigateToMap =
          salesforceContext !== null &&
          salesforceContext.landingPage === SALESFORCE_LANDING_PAGE_MAP;

        if (shouldNavigateToMap) {
          // Salesforce起点の地図遷移を優先し、consumeLoginRedirect()の結果は破棄する
          navigate(buildSalesforceMapRedirectPath(primarySalesforceRealEstate));
        } else {
          navigate(redirect ?? (mypageTop ? "/mypage" : "/feed"));
        }
      } catch (error) {
        // エラーをトラッキングサービスに送信
        Sentry.captureException(error);
        toast.error("認証処理に失敗しました");
        navigate("/login");
      }
    };

    void initializeAuth();
  }, [
    searchParams,
    navigate,
    setCookie,
    userMe,
    geospaceGetMapServiceContract,
    mypageTop,
    apiClient,
  ]);

  return { isInitializing };
};
